/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  AcceptProposalPathParameters,
  AssignProposalPathParameters,
  CommentProposalPathParameters,
  GetFieldMappingImportTemplatePathParameters,
  GetNeighborhoodPathParameters,
  GetProposalPathParameters,
  GetStatsParams,
  ImportFieldMappingProposalsPathParameters,
  ListProposalsParams,
  NeedsInfoProposalPathParameters,
  RejectProposalPathParameters,
  TLHTTPValidationError,
  TLImportFieldMappingProposalsNexusProjectsRequest,
  TLNexusAssignProposalRequest,
  TLNexusBulkActionByFilterRequest,
  TLNexusBulkActionByFilterResponse,
  TLNexusBulkActionRequest,
  TLNexusBulkActionResponse,
  TLNexusCommentRequest,
  TLNexusCreateProposalRequest,
  TLNexusMappingImportResponse,
  TLNexusMappingImportTemplate,
  TLNexusNeighborhoodResponse,
  TLNexusProposalDetailResponse,
  TLNexusProposalResponse,
  TLNexusProposalTransitionRequest,
  TLNexusQueueListResponse,
  TLNexusReviewerStatsResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Import Field Mapping Proposals
 */
export const importFieldMappingProposals = (
  { projectId }: ImportFieldMappingProposalsPathParameters,
  tLImportFieldMappingProposalsNexusProjectsRequest: BodyType<TLImportFieldMappingProposalsNexusProjectsRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  const formData = new FormData();
  formData.append(`file`, tLImportFieldMappingProposalsNexusProjectsRequest.file);

  return apiInstance<TLNexusMappingImportResponse>(
    {
      url: `/nexus/projects/${projectId}/proposals/import`,
      method: 'POST',
      headers: { 'Content-Type': 'multipart/form-data' },
      data: formData,
      signal,
    },
    options,
  );
};

export const getImportFieldMappingProposalsQueryKey = (
  { projectId }: ImportFieldMappingProposalsPathParameters,
  tLImportFieldMappingProposalsNexusProjectsRequest?: BodyType<TLImportFieldMappingProposalsNexusProjectsRequest>,
) => {
  return [
    'POST',
    'nexus',
    'projects',
    projectId,
    'proposals',
    'import',
    tLImportFieldMappingProposalsNexusProjectsRequest,
  ] as const;
};

export const getImportFieldMappingProposalsQueryOptions = <
  TData = Awaited<ReturnType<typeof importFieldMappingProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ImportFieldMappingProposalsPathParameters,
  tLImportFieldMappingProposalsNexusProjectsRequest: BodyType<TLImportFieldMappingProposalsNexusProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importFieldMappingProposals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getImportFieldMappingProposalsQueryKey({ projectId }, tLImportFieldMappingProposalsNexusProjectsRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof importFieldMappingProposals>>> = ({ signal }) =>
    importFieldMappingProposals(
      { projectId },
      tLImportFieldMappingProposalsNexusProjectsRequest,
      requestOptions,
      signal,
    );

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof importFieldMappingProposals>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ImportFieldMappingProposalsQueryResult = NonNullable<
  Awaited<ReturnType<typeof importFieldMappingProposals>>
>;
export type ImportFieldMappingProposalsQueryError = ErrorType<TLHTTPValidationError>;

export function useImportFieldMappingProposals<
  TData = Awaited<ReturnType<typeof importFieldMappingProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ImportFieldMappingProposalsPathParameters,
  tLImportFieldMappingProposalsNexusProjectsRequest: BodyType<TLImportFieldMappingProposalsNexusProjectsRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof importFieldMappingProposals>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof importFieldMappingProposals>>,
          TError,
          Awaited<ReturnType<typeof importFieldMappingProposals>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useImportFieldMappingProposals<
  TData = Awaited<ReturnType<typeof importFieldMappingProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ImportFieldMappingProposalsPathParameters,
  tLImportFieldMappingProposalsNexusProjectsRequest: BodyType<TLImportFieldMappingProposalsNexusProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importFieldMappingProposals>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof importFieldMappingProposals>>,
          TError,
          Awaited<ReturnType<typeof importFieldMappingProposals>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useImportFieldMappingProposals<
  TData = Awaited<ReturnType<typeof importFieldMappingProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ImportFieldMappingProposalsPathParameters,
  tLImportFieldMappingProposalsNexusProjectsRequest: BodyType<TLImportFieldMappingProposalsNexusProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importFieldMappingProposals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Import Field Mapping Proposals
 */

export function useImportFieldMappingProposals<
  TData = Awaited<ReturnType<typeof importFieldMappingProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ImportFieldMappingProposalsPathParameters,
  tLImportFieldMappingProposalsNexusProjectsRequest: BodyType<TLImportFieldMappingProposalsNexusProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importFieldMappingProposals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getImportFieldMappingProposalsQueryOptions(
    { projectId },
    tLImportFieldMappingProposalsNexusProjectsRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Import Field Mapping Proposals
 */
export const prefetchImportFieldMappingProposalsQuery = async <
  TData = Awaited<ReturnType<typeof importFieldMappingProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: ImportFieldMappingProposalsPathParameters,
  tLImportFieldMappingProposalsNexusProjectsRequest: BodyType<TLImportFieldMappingProposalsNexusProjectsRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof importFieldMappingProposals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getImportFieldMappingProposalsQueryOptions(
    { projectId },
    tLImportFieldMappingProposalsNexusProjectsRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Import Field Mapping Proposals
 */
export const invalidateImportFieldMappingProposals = async (
  queryClient: QueryClient,
  { projectId }: ImportFieldMappingProposalsPathParameters,
  tLImportFieldMappingProposalsNexusProjectsRequest: BodyType<TLImportFieldMappingProposalsNexusProjectsRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    {
      queryKey: getImportFieldMappingProposalsQueryKey(
        { projectId },
        tLImportFieldMappingProposalsNexusProjectsRequest,
      ),
    },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Field Mapping Import Template
 */
export const getFieldMappingImportTemplate = (
  { projectId }: GetFieldMappingImportTemplatePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusMappingImportTemplate>(
    { url: `/nexus/projects/${projectId}/proposals/import/template`, method: 'GET', signal },
    options,
  );
};

export const getGetFieldMappingImportTemplateQueryKey = ({
  projectId,
}: GetFieldMappingImportTemplatePathParameters) => {
  return ['nexus', 'projects', projectId, 'proposals', 'import', 'template'] as const;
};

export const getGetFieldMappingImportTemplateQueryOptions = <
  TData = Awaited<ReturnType<typeof getFieldMappingImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GetFieldMappingImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFieldMappingImportTemplate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFieldMappingImportTemplateQueryKey({ projectId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFieldMappingImportTemplate>>> = ({ signal }) =>
    getFieldMappingImportTemplate({ projectId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getFieldMappingImportTemplate>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetFieldMappingImportTemplateQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFieldMappingImportTemplate>>
>;
export type GetFieldMappingImportTemplateQueryError = ErrorType<TLHTTPValidationError>;

export function useGetFieldMappingImportTemplate<
  TData = Awaited<ReturnType<typeof getFieldMappingImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFieldMappingImportTemplatePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFieldMappingImportTemplate>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFieldMappingImportTemplate>>,
          TError,
          Awaited<ReturnType<typeof getFieldMappingImportTemplate>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFieldMappingImportTemplate<
  TData = Awaited<ReturnType<typeof getFieldMappingImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFieldMappingImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFieldMappingImportTemplate>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFieldMappingImportTemplate>>,
          TError,
          Awaited<ReturnType<typeof getFieldMappingImportTemplate>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetFieldMappingImportTemplate<
  TData = Awaited<ReturnType<typeof getFieldMappingImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetFieldMappingImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFieldMappingImportTemplate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Field Mapping Import Template
 */

export function useGetFieldMappingImportTemplate<
  TData = Awaited<ReturnType<typeof getFieldMappingImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: GetFieldMappingImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFieldMappingImportTemplate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetFieldMappingImportTemplateQueryOptions({ projectId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Field Mapping Import Template
 */
export const prefetchGetFieldMappingImportTemplateQuery = async <
  TData = Awaited<ReturnType<typeof getFieldMappingImportTemplate>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: GetFieldMappingImportTemplatePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getFieldMappingImportTemplate>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetFieldMappingImportTemplateQueryOptions({ projectId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Field Mapping Import Template
 */
export const invalidateGetFieldMappingImportTemplate = async (
  queryClient: QueryClient,
  { projectId }: GetFieldMappingImportTemplatePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetFieldMappingImportTemplateQueryKey({ projectId }) }, options);

  return queryClient;
};

/**
 * @summary List Proposals
 */
export const listProposals = (
  params?: ListProposalsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusQueueListResponse>({ url: `/nexus/proposals`, method: 'GET', params, signal }, options);
};

export const getListProposalsQueryKey = (params?: ListProposalsParams) => {
  return ['nexus', 'proposals', ...(params ? [params] : [])] as const;
};

export const getListProposalsQueryOptions = <
  TData = Awaited<ReturnType<typeof listProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListProposalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProposals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListProposalsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listProposals>>> = ({ signal }) =>
    listProposals(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listProposals>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListProposalsQueryResult = NonNullable<Awaited<ReturnType<typeof listProposals>>>;
export type ListProposalsQueryError = ErrorType<TLHTTPValidationError>;

export function useListProposals<
  TData = Awaited<ReturnType<typeof listProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListProposalsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProposals>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProposals>>,
          TError,
          Awaited<ReturnType<typeof listProposals>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListProposals<
  TData = Awaited<ReturnType<typeof listProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListProposalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProposals>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listProposals>>,
          TError,
          Awaited<ReturnType<typeof listProposals>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListProposals<
  TData = Awaited<ReturnType<typeof listProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListProposalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProposals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Proposals
 */

export function useListProposals<
  TData = Awaited<ReturnType<typeof listProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListProposalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProposals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListProposalsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Proposals
 */
export const prefetchListProposalsQuery = async <
  TData = Awaited<ReturnType<typeof listProposals>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListProposalsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listProposals>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListProposalsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Proposals
 */
export const invalidateListProposals = async (
  queryClient: QueryClient,
  params?: ListProposalsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListProposalsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Create Proposal
 */
export const createProposal = (
  tLNexusCreateProposalRequest: BodyType<TLNexusCreateProposalRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusProposalResponse>(
    {
      url: `/nexus/proposals`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusCreateProposalRequest,
      signal,
    },
    options,
  );
};

export const getCreateProposalQueryKey = (tLNexusCreateProposalRequest?: BodyType<TLNexusCreateProposalRequest>) => {
  return ['POST', 'nexus', 'proposals', tLNexusCreateProposalRequest] as const;
};

export const getCreateProposalQueryOptions = <
  TData = Awaited<ReturnType<typeof createProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusCreateProposalRequest: BodyType<TLNexusCreateProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateProposalQueryKey(tLNexusCreateProposalRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createProposal>>> = ({ signal }) =>
    createProposal(tLNexusCreateProposalRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createProposal>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateProposalQueryResult = NonNullable<Awaited<ReturnType<typeof createProposal>>>;
export type CreateProposalQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateProposal<
  TData = Awaited<ReturnType<typeof createProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusCreateProposalRequest: BodyType<TLNexusCreateProposalRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProposal>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createProposal>>,
          TError,
          Awaited<ReturnType<typeof createProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateProposal<
  TData = Awaited<ReturnType<typeof createProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusCreateProposalRequest: BodyType<TLNexusCreateProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProposal>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createProposal>>,
          TError,
          Awaited<ReturnType<typeof createProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateProposal<
  TData = Awaited<ReturnType<typeof createProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusCreateProposalRequest: BodyType<TLNexusCreateProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Proposal
 */

export function useCreateProposal<
  TData = Awaited<ReturnType<typeof createProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusCreateProposalRequest: BodyType<TLNexusCreateProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateProposalQueryOptions(tLNexusCreateProposalRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Proposal
 */
export const prefetchCreateProposalQuery = async <
  TData = Awaited<ReturnType<typeof createProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLNexusCreateProposalRequest: BodyType<TLNexusCreateProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateProposalQueryOptions(tLNexusCreateProposalRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Proposal
 */
export const invalidateCreateProposal = async (
  queryClient: QueryClient,
  tLNexusCreateProposalRequest: BodyType<TLNexusCreateProposalRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateProposalQueryKey(tLNexusCreateProposalRequest) }, options);

  return queryClient;
};

/**
 * @summary Bulk Action
 */
export const bulkAction = (
  tLNexusBulkActionRequest: BodyType<TLNexusBulkActionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusBulkActionResponse>(
    {
      url: `/nexus/proposals/bulk`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusBulkActionRequest,
      signal,
    },
    options,
  );
};

export const getBulkActionQueryKey = (tLNexusBulkActionRequest?: BodyType<TLNexusBulkActionRequest>) => {
  return ['POST', 'nexus', 'proposals', 'bulk', tLNexusBulkActionRequest] as const;
};

export const getBulkActionQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkAction>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionRequest: BodyType<TLNexusBulkActionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAction>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkActionQueryKey(tLNexusBulkActionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkAction>>> = ({ signal }) =>
    bulkAction(tLNexusBulkActionRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkAction>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkActionQueryResult = NonNullable<Awaited<ReturnType<typeof bulkAction>>>;
export type BulkActionQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkAction<
  TData = Awaited<ReturnType<typeof bulkAction>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionRequest: BodyType<TLNexusBulkActionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAction>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkAction>>,
          TError,
          Awaited<ReturnType<typeof bulkAction>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkAction<
  TData = Awaited<ReturnType<typeof bulkAction>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionRequest: BodyType<TLNexusBulkActionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAction>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkAction>>,
          TError,
          Awaited<ReturnType<typeof bulkAction>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkAction<
  TData = Awaited<ReturnType<typeof bulkAction>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionRequest: BodyType<TLNexusBulkActionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAction>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Action
 */

export function useBulkAction<
  TData = Awaited<ReturnType<typeof bulkAction>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionRequest: BodyType<TLNexusBulkActionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAction>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkActionQueryOptions(tLNexusBulkActionRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Action
 */
export const prefetchBulkActionQuery = async <
  TData = Awaited<ReturnType<typeof bulkAction>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLNexusBulkActionRequest: BodyType<TLNexusBulkActionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkAction>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkActionQueryOptions(tLNexusBulkActionRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Action
 */
export const invalidateBulkAction = async (
  queryClient: QueryClient,
  tLNexusBulkActionRequest: BodyType<TLNexusBulkActionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getBulkActionQueryKey(tLNexusBulkActionRequest) }, options);

  return queryClient;
};

/**
 * @summary Bulk Action By Filter
 */
export const bulkActionByFilter = (
  tLNexusBulkActionByFilterRequest: BodyType<TLNexusBulkActionByFilterRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusBulkActionByFilterResponse>(
    {
      url: `/nexus/proposals/bulk-by-filter`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusBulkActionByFilterRequest,
      signal,
    },
    options,
  );
};

export const getBulkActionByFilterQueryKey = (
  tLNexusBulkActionByFilterRequest?: BodyType<TLNexusBulkActionByFilterRequest>,
) => {
  return ['POST', 'nexus', 'proposals', 'bulk-by-filter', tLNexusBulkActionByFilterRequest] as const;
};

export const getBulkActionByFilterQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkActionByFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionByFilterRequest: BodyType<TLNexusBulkActionByFilterRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkActionByFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkActionByFilterQueryKey(tLNexusBulkActionByFilterRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkActionByFilter>>> = ({ signal }) =>
    bulkActionByFilter(tLNexusBulkActionByFilterRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkActionByFilter>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkActionByFilterQueryResult = NonNullable<Awaited<ReturnType<typeof bulkActionByFilter>>>;
export type BulkActionByFilterQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkActionByFilter<
  TData = Awaited<ReturnType<typeof bulkActionByFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionByFilterRequest: BodyType<TLNexusBulkActionByFilterRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkActionByFilter>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkActionByFilter>>,
          TError,
          Awaited<ReturnType<typeof bulkActionByFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkActionByFilter<
  TData = Awaited<ReturnType<typeof bulkActionByFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionByFilterRequest: BodyType<TLNexusBulkActionByFilterRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkActionByFilter>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkActionByFilter>>,
          TError,
          Awaited<ReturnType<typeof bulkActionByFilter>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkActionByFilter<
  TData = Awaited<ReturnType<typeof bulkActionByFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionByFilterRequest: BodyType<TLNexusBulkActionByFilterRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkActionByFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Action By Filter
 */

export function useBulkActionByFilter<
  TData = Awaited<ReturnType<typeof bulkActionByFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLNexusBulkActionByFilterRequest: BodyType<TLNexusBulkActionByFilterRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkActionByFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkActionByFilterQueryOptions(tLNexusBulkActionByFilterRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Action By Filter
 */
export const prefetchBulkActionByFilterQuery = async <
  TData = Awaited<ReturnType<typeof bulkActionByFilter>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLNexusBulkActionByFilterRequest: BodyType<TLNexusBulkActionByFilterRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkActionByFilter>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkActionByFilterQueryOptions(tLNexusBulkActionByFilterRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Action By Filter
 */
export const invalidateBulkActionByFilter = async (
  queryClient: QueryClient,
  tLNexusBulkActionByFilterRequest: BodyType<TLNexusBulkActionByFilterRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkActionByFilterQueryKey(tLNexusBulkActionByFilterRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Stats
 */
export const getStats = (
  params?: GetStatsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusReviewerStatsResponse>(
    { url: `/nexus/proposals/stats`, method: 'GET', params, signal },
    options,
  );
};

export const getGetStatsQueryKey = (params?: GetStatsParams) => {
  return ['nexus', 'proposals', 'stats', ...(params ? [params] : [])] as const;
};

export const getGetStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetStatsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getStats>>> = ({ signal }) =>
    getStats(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getStats>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getStats>>>;
export type GetStatsQueryError = ErrorType<TLHTTPValidationError>;

export function useGetStats<TData = Awaited<ReturnType<typeof getStats>>, TError = ErrorType<TLHTTPValidationError>>(
  params: undefined | GetStatsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getStats>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<Awaited<ReturnType<typeof getStats>>, TError, Awaited<ReturnType<typeof getStats>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetStats<TData = Awaited<ReturnType<typeof getStats>>, TError = ErrorType<TLHTTPValidationError>>(
  params?: GetStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getStats>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<Awaited<ReturnType<typeof getStats>>, TError, Awaited<ReturnType<typeof getStats>>>,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetStats<TData = Awaited<ReturnType<typeof getStats>>, TError = ErrorType<TLHTTPValidationError>>(
  params?: GetStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Stats
 */

export function useGetStats<TData = Awaited<ReturnType<typeof getStats>>, TError = ErrorType<TLHTTPValidationError>>(
  params?: GetStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetStatsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Stats
 */
export const prefetchGetStatsQuery = async <
  TData = Awaited<ReturnType<typeof getStats>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetStatsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getStats>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetStatsQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Stats
 */
export const invalidateGetStats = async (
  queryClient: QueryClient,
  params?: GetStatsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetStatsQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Get Proposal
 */
export const getProposal = (
  { proposalId }: GetProposalPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusProposalDetailResponse>(
    { url: `/nexus/proposals/${proposalId}`, method: 'GET', signal },
    options,
  );
};

export const getGetProposalQueryKey = ({ proposalId }: GetProposalPathParameters) => {
  return ['nexus', 'proposals', proposalId] as const;
};

export const getGetProposalQueryOptions = <
  TData = Awaited<ReturnType<typeof getProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: GetProposalPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProposalQueryKey({ proposalId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProposal>>> = ({ signal }) =>
    getProposal({ proposalId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: proposalId !== null && proposalId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getProposal>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetProposalQueryResult = NonNullable<Awaited<ReturnType<typeof getProposal>>>;
export type GetProposalQueryError = ErrorType<TLHTTPValidationError>;

export function useGetProposal<
  TData = Awaited<ReturnType<typeof getProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProposalPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposal>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProposal>>,
          TError,
          Awaited<ReturnType<typeof getProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProposal<
  TData = Awaited<ReturnType<typeof getProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProposalPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposal>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProposal>>,
          TError,
          Awaited<ReturnType<typeof getProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProposal<
  TData = Awaited<ReturnType<typeof getProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetProposalPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Proposal
 */

export function useGetProposal<
  TData = Awaited<ReturnType<typeof getProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: GetProposalPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProposalQueryOptions({ proposalId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Proposal
 */
export const prefetchGetProposalQuery = async <
  TData = Awaited<ReturnType<typeof getProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { proposalId }: GetProposalPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetProposalQueryOptions({ proposalId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Proposal
 */
export const invalidateGetProposal = async (
  queryClient: QueryClient,
  { proposalId }: GetProposalPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetProposalQueryKey({ proposalId }) }, options);

  return queryClient;
};

/**
 * @summary Accept Proposal
 */
export const acceptProposal = (
  { proposalId }: AcceptProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusProposalResponse>(
    {
      url: `/nexus/proposals/${proposalId}/accept`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusProposalTransitionRequest,
      signal,
    },
    options,
  );
};

export const getAcceptProposalQueryKey = (
  { proposalId }: AcceptProposalPathParameters,
  tLNexusProposalTransitionRequest?: BodyType<TLNexusProposalTransitionRequest>,
) => {
  return ['POST', 'nexus', 'proposals', proposalId, 'accept', tLNexusProposalTransitionRequest] as const;
};

export const getAcceptProposalQueryOptions = <
  TData = Awaited<ReturnType<typeof acceptProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: AcceptProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAcceptProposalQueryKey({ proposalId }, tLNexusProposalTransitionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof acceptProposal>>> = ({ signal }) =>
    acceptProposal({ proposalId }, tLNexusProposalTransitionRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: proposalId !== null && proposalId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof acceptProposal>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AcceptProposalQueryResult = NonNullable<Awaited<ReturnType<typeof acceptProposal>>>;
export type AcceptProposalQueryError = ErrorType<TLHTTPValidationError>;

export function useAcceptProposal<
  TData = Awaited<ReturnType<typeof acceptProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AcceptProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptProposal>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof acceptProposal>>,
          TError,
          Awaited<ReturnType<typeof acceptProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptProposal<
  TData = Awaited<ReturnType<typeof acceptProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AcceptProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptProposal>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof acceptProposal>>,
          TError,
          Awaited<ReturnType<typeof acceptProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptProposal<
  TData = Awaited<ReturnType<typeof acceptProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AcceptProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Accept Proposal
 */

export function useAcceptProposal<
  TData = Awaited<ReturnType<typeof acceptProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: AcceptProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAcceptProposalQueryOptions({ proposalId }, tLNexusProposalTransitionRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Accept Proposal
 */
export const prefetchAcceptProposalQuery = async <
  TData = Awaited<ReturnType<typeof acceptProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { proposalId }: AcceptProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof acceptProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAcceptProposalQueryOptions({ proposalId }, tLNexusProposalTransitionRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Accept Proposal
 */
export const invalidateAcceptProposal = async (
  queryClient: QueryClient,
  { proposalId }: AcceptProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAcceptProposalQueryKey({ proposalId }, tLNexusProposalTransitionRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Assign Proposal
 */
export const assignProposal = (
  { proposalId }: AssignProposalPathParameters,
  tLNexusAssignProposalRequest: BodyType<TLNexusAssignProposalRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusProposalResponse>(
    {
      url: `/nexus/proposals/${proposalId}/assign`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusAssignProposalRequest,
      signal,
    },
    options,
  );
};

export const getAssignProposalQueryKey = (
  { proposalId }: AssignProposalPathParameters,
  tLNexusAssignProposalRequest?: BodyType<TLNexusAssignProposalRequest>,
) => {
  return ['POST', 'nexus', 'proposals', proposalId, 'assign', tLNexusAssignProposalRequest] as const;
};

export const getAssignProposalQueryOptions = <
  TData = Awaited<ReturnType<typeof assignProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: AssignProposalPathParameters,
  tLNexusAssignProposalRequest: BodyType<TLNexusAssignProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getAssignProposalQueryKey({ proposalId }, tLNexusAssignProposalRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof assignProposal>>> = ({ signal }) =>
    assignProposal({ proposalId }, tLNexusAssignProposalRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: proposalId !== null && proposalId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof assignProposal>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type AssignProposalQueryResult = NonNullable<Awaited<ReturnType<typeof assignProposal>>>;
export type AssignProposalQueryError = ErrorType<TLHTTPValidationError>;

export function useAssignProposal<
  TData = Awaited<ReturnType<typeof assignProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignProposalPathParameters,
  tLNexusAssignProposalRequest: BodyType<TLNexusAssignProposalRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignProposal>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof assignProposal>>,
          TError,
          Awaited<ReturnType<typeof assignProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAssignProposal<
  TData = Awaited<ReturnType<typeof assignProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignProposalPathParameters,
  tLNexusAssignProposalRequest: BodyType<TLNexusAssignProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignProposal>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof assignProposal>>,
          TError,
          Awaited<ReturnType<typeof assignProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAssignProposal<
  TData = Awaited<ReturnType<typeof assignProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: AssignProposalPathParameters,
  tLNexusAssignProposalRequest: BodyType<TLNexusAssignProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Assign Proposal
 */

export function useAssignProposal<
  TData = Awaited<ReturnType<typeof assignProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: AssignProposalPathParameters,
  tLNexusAssignProposalRequest: BodyType<TLNexusAssignProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAssignProposalQueryOptions({ proposalId }, tLNexusAssignProposalRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Assign Proposal
 */
export const prefetchAssignProposalQuery = async <
  TData = Awaited<ReturnType<typeof assignProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { proposalId }: AssignProposalPathParameters,
  tLNexusAssignProposalRequest: BodyType<TLNexusAssignProposalRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof assignProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getAssignProposalQueryOptions({ proposalId }, tLNexusAssignProposalRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Assign Proposal
 */
export const invalidateAssignProposal = async (
  queryClient: QueryClient,
  { proposalId }: AssignProposalPathParameters,
  tLNexusAssignProposalRequest: BodyType<TLNexusAssignProposalRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getAssignProposalQueryKey({ proposalId }, tLNexusAssignProposalRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Comment Proposal
 */
export const commentProposal = (
  { proposalId }: CommentProposalPathParameters,
  tLNexusCommentRequest: BodyType<TLNexusCommentRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusProposalResponse>(
    {
      url: `/nexus/proposals/${proposalId}/comment`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusCommentRequest,
      signal,
    },
    options,
  );
};

export const getCommentProposalQueryKey = (
  { proposalId }: CommentProposalPathParameters,
  tLNexusCommentRequest?: BodyType<TLNexusCommentRequest>,
) => {
  return ['POST', 'nexus', 'proposals', proposalId, 'comment', tLNexusCommentRequest] as const;
};

export const getCommentProposalQueryOptions = <
  TData = Awaited<ReturnType<typeof commentProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: CommentProposalPathParameters,
  tLNexusCommentRequest: BodyType<TLNexusCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof commentProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCommentProposalQueryKey({ proposalId }, tLNexusCommentRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof commentProposal>>> = ({ signal }) =>
    commentProposal({ proposalId }, tLNexusCommentRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: proposalId !== null && proposalId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof commentProposal>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CommentProposalQueryResult = NonNullable<Awaited<ReturnType<typeof commentProposal>>>;
export type CommentProposalQueryError = ErrorType<TLHTTPValidationError>;

export function useCommentProposal<
  TData = Awaited<ReturnType<typeof commentProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CommentProposalPathParameters,
  tLNexusCommentRequest: BodyType<TLNexusCommentRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof commentProposal>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof commentProposal>>,
          TError,
          Awaited<ReturnType<typeof commentProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCommentProposal<
  TData = Awaited<ReturnType<typeof commentProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CommentProposalPathParameters,
  tLNexusCommentRequest: BodyType<TLNexusCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof commentProposal>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof commentProposal>>,
          TError,
          Awaited<ReturnType<typeof commentProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCommentProposal<
  TData = Awaited<ReturnType<typeof commentProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CommentProposalPathParameters,
  tLNexusCommentRequest: BodyType<TLNexusCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof commentProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Comment Proposal
 */

export function useCommentProposal<
  TData = Awaited<ReturnType<typeof commentProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: CommentProposalPathParameters,
  tLNexusCommentRequest: BodyType<TLNexusCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof commentProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCommentProposalQueryOptions({ proposalId }, tLNexusCommentRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Comment Proposal
 */
export const prefetchCommentProposalQuery = async <
  TData = Awaited<ReturnType<typeof commentProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { proposalId }: CommentProposalPathParameters,
  tLNexusCommentRequest: BodyType<TLNexusCommentRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof commentProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCommentProposalQueryOptions({ proposalId }, tLNexusCommentRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Comment Proposal
 */
export const invalidateCommentProposal = async (
  queryClient: QueryClient,
  { proposalId }: CommentProposalPathParameters,
  tLNexusCommentRequest: BodyType<TLNexusCommentRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCommentProposalQueryKey({ proposalId }, tLNexusCommentRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Needs Info Proposal
 */
export const needsInfoProposal = (
  { proposalId }: NeedsInfoProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusProposalResponse>(
    {
      url: `/nexus/proposals/${proposalId}/needs-info`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusProposalTransitionRequest,
      signal,
    },
    options,
  );
};

export const getNeedsInfoProposalQueryKey = (
  { proposalId }: NeedsInfoProposalPathParameters,
  tLNexusProposalTransitionRequest?: BodyType<TLNexusProposalTransitionRequest>,
) => {
  return ['POST', 'nexus', 'proposals', proposalId, 'needs-info', tLNexusProposalTransitionRequest] as const;
};

export const getNeedsInfoProposalQueryOptions = <
  TData = Awaited<ReturnType<typeof needsInfoProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: NeedsInfoProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof needsInfoProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getNeedsInfoProposalQueryKey({ proposalId }, tLNexusProposalTransitionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof needsInfoProposal>>> = ({ signal }) =>
    needsInfoProposal({ proposalId }, tLNexusProposalTransitionRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: proposalId !== null && proposalId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof needsInfoProposal>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type NeedsInfoProposalQueryResult = NonNullable<Awaited<ReturnType<typeof needsInfoProposal>>>;
export type NeedsInfoProposalQueryError = ErrorType<TLHTTPValidationError>;

export function useNeedsInfoProposal<
  TData = Awaited<ReturnType<typeof needsInfoProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: NeedsInfoProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof needsInfoProposal>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof needsInfoProposal>>,
          TError,
          Awaited<ReturnType<typeof needsInfoProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useNeedsInfoProposal<
  TData = Awaited<ReturnType<typeof needsInfoProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: NeedsInfoProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof needsInfoProposal>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof needsInfoProposal>>,
          TError,
          Awaited<ReturnType<typeof needsInfoProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useNeedsInfoProposal<
  TData = Awaited<ReturnType<typeof needsInfoProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: NeedsInfoProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof needsInfoProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Needs Info Proposal
 */

export function useNeedsInfoProposal<
  TData = Awaited<ReturnType<typeof needsInfoProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: NeedsInfoProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof needsInfoProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getNeedsInfoProposalQueryOptions({ proposalId }, tLNexusProposalTransitionRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Needs Info Proposal
 */
export const prefetchNeedsInfoProposalQuery = async <
  TData = Awaited<ReturnType<typeof needsInfoProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { proposalId }: NeedsInfoProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof needsInfoProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getNeedsInfoProposalQueryOptions({ proposalId }, tLNexusProposalTransitionRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Needs Info Proposal
 */
export const invalidateNeedsInfoProposal = async (
  queryClient: QueryClient,
  { proposalId }: NeedsInfoProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getNeedsInfoProposalQueryKey({ proposalId }, tLNexusProposalTransitionRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Neighborhood
 */
export const getNeighborhood = (
  { proposalId }: GetNeighborhoodPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusNeighborhoodResponse>(
    { url: `/nexus/proposals/${proposalId}/neighborhood`, method: 'GET', signal },
    options,
  );
};

export const getGetNeighborhoodQueryKey = ({ proposalId }: GetNeighborhoodPathParameters) => {
  return ['nexus', 'proposals', proposalId, 'neighborhood'] as const;
};

export const getGetNeighborhoodQueryOptions = <
  TData = Awaited<ReturnType<typeof getNeighborhood>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: GetNeighborhoodPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNeighborhood>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetNeighborhoodQueryKey({ proposalId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getNeighborhood>>> = ({ signal }) =>
    getNeighborhood({ proposalId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: proposalId !== null && proposalId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getNeighborhood>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetNeighborhoodQueryResult = NonNullable<Awaited<ReturnType<typeof getNeighborhood>>>;
export type GetNeighborhoodQueryError = ErrorType<TLHTTPValidationError>;

export function useGetNeighborhood<
  TData = Awaited<ReturnType<typeof getNeighborhood>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetNeighborhoodPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNeighborhood>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNeighborhood>>,
          TError,
          Awaited<ReturnType<typeof getNeighborhood>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNeighborhood<
  TData = Awaited<ReturnType<typeof getNeighborhood>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetNeighborhoodPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNeighborhood>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNeighborhood>>,
          TError,
          Awaited<ReturnType<typeof getNeighborhood>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetNeighborhood<
  TData = Awaited<ReturnType<typeof getNeighborhood>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetNeighborhoodPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNeighborhood>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Neighborhood
 */

export function useGetNeighborhood<
  TData = Awaited<ReturnType<typeof getNeighborhood>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: GetNeighborhoodPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNeighborhood>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetNeighborhoodQueryOptions({ proposalId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Neighborhood
 */
export const prefetchGetNeighborhoodQuery = async <
  TData = Awaited<ReturnType<typeof getNeighborhood>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { proposalId }: GetNeighborhoodPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getNeighborhood>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetNeighborhoodQueryOptions({ proposalId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Neighborhood
 */
export const invalidateGetNeighborhood = async (
  queryClient: QueryClient,
  { proposalId }: GetNeighborhoodPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetNeighborhoodQueryKey({ proposalId }) }, options);

  return queryClient;
};

/**
 * @summary Reject Proposal
 */
export const rejectProposal = (
  { proposalId }: RejectProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusProposalResponse>(
    {
      url: `/nexus/proposals/${proposalId}/reject`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusProposalTransitionRequest,
      signal,
    },
    options,
  );
};

export const getRejectProposalQueryKey = (
  { proposalId }: RejectProposalPathParameters,
  tLNexusProposalTransitionRequest?: BodyType<TLNexusProposalTransitionRequest>,
) => {
  return ['POST', 'nexus', 'proposals', proposalId, 'reject', tLNexusProposalTransitionRequest] as const;
};

export const getRejectProposalQueryOptions = <
  TData = Awaited<ReturnType<typeof rejectProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: RejectProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getRejectProposalQueryKey({ proposalId }, tLNexusProposalTransitionRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof rejectProposal>>> = ({ signal }) =>
    rejectProposal({ proposalId }, tLNexusProposalTransitionRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: proposalId !== null && proposalId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof rejectProposal>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type RejectProposalQueryResult = NonNullable<Awaited<ReturnType<typeof rejectProposal>>>;
export type RejectProposalQueryError = ErrorType<TLHTTPValidationError>;

export function useRejectProposal<
  TData = Awaited<ReturnType<typeof rejectProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RejectProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectProposal>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof rejectProposal>>,
          TError,
          Awaited<ReturnType<typeof rejectProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRejectProposal<
  TData = Awaited<ReturnType<typeof rejectProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RejectProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectProposal>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof rejectProposal>>,
          TError,
          Awaited<ReturnType<typeof rejectProposal>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useRejectProposal<
  TData = Awaited<ReturnType<typeof rejectProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: RejectProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Reject Proposal
 */

export function useRejectProposal<
  TData = Awaited<ReturnType<typeof rejectProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { proposalId }: RejectProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getRejectProposalQueryOptions({ proposalId }, tLNexusProposalTransitionRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Reject Proposal
 */
export const prefetchRejectProposalQuery = async <
  TData = Awaited<ReturnType<typeof rejectProposal>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { proposalId }: RejectProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof rejectProposal>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getRejectProposalQueryOptions({ proposalId }, tLNexusProposalTransitionRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Reject Proposal
 */
export const invalidateRejectProposal = async (
  queryClient: QueryClient,
  { proposalId }: RejectProposalPathParameters,
  tLNexusProposalTransitionRequest: BodyType<TLNexusProposalTransitionRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getRejectProposalQueryKey({ proposalId }, tLNexusProposalTransitionRequest) },
    options,
  );

  return queryClient;
};
